import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import jwtDecode from "jwt-decode";
import { authActions } from "src/context/auth-slice";
import { adminList, rolesToRestrict } from "src/constants/roles";
import { getOrCreateRefresh } from "src/utils/refreshLock";
import { tokenStore } from "src/utils/tokenStore";

/**
 * Run on app mount, BEFORE the auth gate. Tries to restore a session from
 * the backend's httpOnly refresh cookie (sent automatically because
 * /auth/refresh is called with `withCredentials: true`). If the cookie is
 * valid, the call returns fresh tokens, we hydrate Redux, and the user
 * skips <Login /> entirely — that's how reload + cross-tab persistence
 * works.
 *
 * If the cookie is absent or rejected (first-ever visit, expired session,
 * cleared cookies), the call rejects, we fall through to <Login />.
 *
 * Returns `bootstrapping` — true while the refresh is in flight; the
 * caller should render a Loader during that window so neither <Login />
 * nor the dashboard flashes briefly.
 */
export const useAuthBootstrap = (): boolean => {
  const dispatch = useDispatch();
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const refreshed = await getOrCreateRefresh();
        if (cancelled || !refreshed.idToken) {
          if (!cancelled) setBootstrapping(false);
          return;
        }

        const decoded: any = jwtDecode(refreshed.idToken);
        const mvRole: string | undefined = decoded?.["custom:mv_role"];
        const isStockist = mvRole === "Stockist" || decoded?.["custom:role"] === "Stockist";

        dispatch(authActions.login(decoded));
        dispatch(authActions.setIdTokenDetails(decoded));
        dispatch(authActions.setLoggedInUser(decoded?.name || ""));
        dispatch(authActions.setIsAdmin(adminList.includes(mvRole as string)));
        dispatch(authActions.setIsClient(mvRole === "Client"));
        dispatch(
          authActions.setRestricted(
            rolesToRestrict.includes(mvRole as string) || !!mvRole?.startsWith("L") || isStockist
          )
        );
      } catch {
        // No valid session cookie — fall through to <Login />.
        // Do NOT call logout() here: there's nothing to log out from yet,
        // and a redirect to Cognito would interrupt the user's first visit.
        tokenStore.clearTokens();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return bootstrapping;
};
